import React from "react";
import { useTranslation } from "react-i18next";
import { BeatLoader } from "react-spinners";
import { MediaDetailPrediction } from "../common/ObjectTypes";
import { tailwindClassnames } from "../common/TailwindClassnames";
import {
  CandidateSpecies,
  buildCandidateSpecies,
  hasCandidateDispositions,
  isSelectableCandidate,
  selectableCandidates,
} from "./candidateSpecies";

/** Legacy fallback only: how many raw labels the pre-AKL-777 list showed. */
const TOP_K = 5;

const pct = (value: number): string => `${Math.round(value * 100)}%`;

/**
 * Join a species row's per-model scores in the active locale.
 *
 * `Intl.ListFormat` rather than a hardcoded separator, matching AKL-780's
 * provenance line: the four shipped locales do not share one list separator.
 * The `typeof` guard is a defensive fallback for a runtime without it - every
 * supported browser has it - so a missing Intl cannot throw during render and
 * blank the popover.
 * @param t translator
 * @param language active i18n language code
 * @param models the row's contributing models, highest score first
 * @returns the joined breakdown, e.g. "SpeciesNet 62%, DeepFaune 24%"
 */
const modelBreakdown = (
  t: (key: string, options?: Record<string, unknown>) => string,
  language: string,
  models: CandidateSpecies["models"],
): string => {
  const parts = models.map((m) =>
    t("{{model}} {{score}}", { model: m.modelName, score: pct(m.score) }),
  );
  return typeof Intl.ListFormat === "function"
    ? new Intl.ListFormat(language, { style: "long", type: "unit" }).format(
        parts,
      )
    : parts.join(", ");
};

/** The label an inert row shows, naming why it cannot be committed. */
const inertLabel = (
  t: (key: string, options?: Record<string, unknown>) => string,
  row: CandidateSpecies,
): string =>
  row.disposition === "out_of_list"
    ? t("{{species}} — not in species list", { species: row.displayName })
    : t("{{label}} — not mapped", { label: row.rawLabel });

type PredictionPopoverProps = {
  /**
   * Every label-bearing prediction on the selected box (AKL-777). Was a single
   * `prediction`, which is what limited the popover to one model - with two
   * classifiers on a box the second's answer was unreachable from the image.
   */
  predictions: MediaDetailPrediction[];
  /**
   * Which of `predictions` belong to this box, from
   * `MediaDetailGeometry.label_prediction_ids`. Omit to use all of them, which
   * is what an API predating AKL-777 gets.
   */
  labelPredictionIds?: number[];
  /**
   * Which of `predictions` produced the label the box actually SHOWS
   * (`MediaDetailGeometry.label_prediction_id`). Named explicitly rather than
   * taken as `predictions[0]`: `label_prediction_ids` is ordered by prediction
   * id and includes the parent detection, so on a crop-classified object index
   * 0 is the DETECTOR — and the trailing provenance line would name
   * MegaDetector instead of the classifier whose label the box shows.
   */
  labelSourcePredictionId?: number;
  /** AKL-777: the taxon the reviewer has picked for this box, if any. */
  selectedTaxonId?: number | null;
  /**
   * AKL-777: fired when a species row is picked. **Omitting this renders the
   * list read-only** - which is how assign-species mode and an
   * already-committed box keep their candidates inert.
   */
  onSelectTaxon?: (taxonId: number, displayName: string) => void;
  anchor: { x: number; y: number };
  onClose: () => void;
  /**
   * Per-object commit handlers (AKL-682). Omit for a read-only popover.
   * Accept/Reject only render for a still-pending or rejected box; Delete
   * only renders for a box already committed to an observation (AKL-712) —
   * pass whichever of these four are meaningful for the box's current state
   * and the popover picks the right set based on `reviewed` below.
   */
  onAccept?: () => void;
  onReject?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  /** Disables the buttons while an action is committing. */
  busy?: boolean;
  /** Which button is mid-commit, so it shows a spinner. */
  pending?: "accept" | "reject" | null;
  /**
   * The box's current review resolution, when it has one. `prediction.labels`
   * below is always the AI's raw, immutable output — it never reflects an
   * edit — so without this the popover can show a stale species (e.g.
   * "warthog") next to a box already edited to "elephant". When present, a
   * chip names the current resolved state.
   *
   * Also drives which action buttons render (AKL-712): a box committed to an
   * observation (`rejected: false`, has a `label`) shows Edit/Delete, acting
   * on that observation; a still-pending box (`reviewed` omitted) or a
   * rejected one (`rejected: true`, no `label`) shows Accept/Reject/Edit,
   * acting on the raw prediction — Accept/Reject on an already-committed
   * observation has nothing left to accept or reject.
   */
  reviewed?: { rejected: boolean; label: string | null } | null;
  /**
   * AKL-717: the box is an `unresolved` detection — its only label was excluded
   * by the project, so there is no species to show and nothing to accept. The
   * popover then explains that in one line, never renders Accept, and offers
   * Reject plus "Assign species" (`onEdit`) in place of Edit.
   */
  unresolved?: boolean;
};

/**
 * Per-object detail + action popover for a selected bounding box (§6.2.2 /
 * §6.4): the candidate species every model proposed for it, model provenance,
 * and — when commit handlers are supplied (AKL-682) — either Accept/Reject/Edit
 * (pending or rejected box) or Edit/Delete (a box already committed to an
 * observation, AKL-712), decided by `reviewed`. Positioned (fixed) near the
 * click anchor.
 *
 * AKL-777: the list was one prediction's top-5 RAW labels; it is now one row
 * per distinct candidate **species** across every model that labelled the box,
 * de-duplicated by taxon. Committable rows are a radio group whose selection
 * relabels the box and decides what Accept commits — but **selection never
 * commits anything itself**. Rows the reviewer must not commit render inert and
 * say why; excluded alternatives are dropped upstream and never arrive here,
 * which is what closes the old spec OQ-6 hazard of a reviewer being able to
 * pick a taxon the project had explicitly excluded.
 *
 * AKL-717: for an `unresolved` box (every label excluded, no fallback) the
 * list is empty by construction. The popover says so in one line and offers
 * Reject and "Assign species" — the latter is `onEdit`, opening the editor
 * with nothing preselected — and never Accept, since nothing was proposed.
 */
const PredictionPopover: React.FC<PredictionPopoverProps> = ({
  predictions,
  labelPredictionIds,
  labelSourcePredictionId,
  selectedTaxonId,
  onSelectTaxon,
  anchor,
  onClose,
  onAccept,
  onReject,
  onEdit,
  onDelete,
  busy,
  pending,
  reviewed,
  unresolved = false,
}) => {
  const { t, i18n } = useTranslation();
  const panelRef = React.useRef<HTMLDivElement | null>(null);
  const [measuredHeight, setMeasuredHeight] = React.useState<number | null>(
    null,
  );
  // AKL-777: spec OQ-6 is closed. Excluded alternatives no longer reach this
  // component at all - the API tags each candidate with a disposition and
  // `buildCandidateSpecies` drops the suppressed ones (and, failing closed,
  // any label whose disposition is absent).
  // A `name` unique to this popover instance. Without a shared name the inputs
  // are not a native radio group at all - arrow keys do not move between them,
  // which is the keyboard behaviour a radiogroup promises. Scoping the name to
  // the box keeps it isolated from any other radio group on screen, which is
  // what omitting the name was originally (wrongly) trying to achieve.
  const radioGroupName = `popover-species-${labelSourcePredictionId ?? predictions[0]?.prediction_id ?? "none"}`;
  const speciesRows = React.useMemo(
    () => buildCandidateSpecies(predictions, labelPredictionIds),
    [predictions, labelPredictionIds],
  );
  // Only announce a radio group when there is actually something to choose.
  // Gated on SELECTABLE rows, not on `speciesRows` — a box whose candidates are
  // all off-roster or unmapped has rows but no choice, and announcing
  // "Choose a species…, radio group" over zero radios tells a screen-reader
  // user a choice exists that does not.
  const canPick =
    onSelectTaxon != null && selectableCandidates(speciesRows).length > 0;
  // The prediction whose label the box shows - its model is the one the
  // trailing provenance line names. Falls back to the first entry only when the
  // caller does not say (an API predating AKL-777 sends one prediction, so the
  // fallback is exact there).
  const prediction =
    predictions.find((p) => p.prediction_id === labelSourcePredictionId) ??
    predictions[0];
  // AKL-777: tell "this API predates dispositions" apart from "dispositions
  // exist and everything was suppressed". `buildCandidateSpecies` yields an
  // empty list for both (it fails closed), but they want opposite treatments —
  // see the legacy branch in the render below.
  const legacyLabels = React.useMemo(() => {
    // AKL-717: an unresolved box never shows a raw label — the only one it has
    // is the label the project excluded. An API that can send `unresolved`
    // always sends dispositions too, so this is belt-and-braces; the no-leak
    // rule is enforced here rather than trusted to hold upstream.
    if (unresolved) return [];
    if (hasCandidateDispositions(predictions)) return [];
    return (prediction?.labels ?? []).slice(0, TOP_K);
  }, [predictions, prediction, unresolved]);
  // AKL-712: a box already committed to an observation has nothing left to
  // accept/reject — only a still-pending or rejected box does.
  const isCommitted = reviewed != null && !reviewed.rejected;
  const hasActions = isCommitted
    ? Boolean(onEdit || onDelete)
    : Boolean(onAccept || onReject || onEdit);

  // Keep the panel inside the viewport with a right/bottom clamp.
  //
  // AKL-777 replaced a fixed `estimatedHeight` with a measured one. The old
  // guess (220px, +60 with a reviewed chip) described a fixed-length list; the
  // species list is variable, so with three models and long names the guess
  // ran short and the panel clipped off the bottom of the screen. The estimate
  // survives only as the pre-measure value for the very first paint, so the
  // panel is not wildly misplaced for one frame.
  const estimatedHeight = 240 + speciesRows.length * 34 + (reviewed ? 60 : 0);
  const clampHeight = measuredHeight ?? estimatedHeight;
  const left = Math.min(anchor.x + 12, window.innerWidth - 336);
  const top = Math.min(anchor.y + 12, window.innerHeight - clampHeight);

  React.useLayoutEffect(() => {
    const height = panelRef.current?.offsetHeight;
    if (height != null && height > 0) setMeasuredHeight(height);
    // Re-measure whenever anything that changes the panel's height changes.
    // Not `measuredHeight` itself, which would loop. `unresolved` adds the
    // explanatory line (AKL-717), so it changes the height too.
  }, [speciesRows, reviewed, hasActions, isCommitted, unresolved]);

  return (
    <div
      ref={panelRef}
      className="fixed z-[1100] w-80 rounded border border-black/10 bg-primary-off-white p-3 text-off-black-light shadow-lg dark:border-white/10 dark:bg-storm-blue dark:text-primary-off-white"
      style={{ left: Math.max(8, left), top: Math.max(8, top) }}
      data-testid="prediction-popover"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold text-storm-blue dark:text-sky-blue-light">
          {t("Detection details")}
        </span>
        <button
          type="button"
          className="text-off-black-light hover:opacity-70 focus-visible:ring-2 focus-visible:ring-sky-blue-light focus:outline-none dark:text-primary-off-white"
          onClick={onClose}
          aria-label={t("Close details")}
          data-testid="prediction-popover-close"
        >
          ✕
        </button>
      </div>

      {reviewed && (
        <div className="mb-2 flex items-center gap-2">
          <span
            className={
              reviewed.rejected
                ? tailwindClassnames.resolvedChipRejected
                : tailwindClassnames.resolvedChipAccepted
            }
            data-testid="popover-reviewed-chip"
          >
            {reviewed.rejected
              ? t("Rejected")
              : t("Reviewed: {{label}}", {
                  label: reviewed.label || t("Unknown"),
                })}
          </span>
        </div>
      )}

      {/* The whole section drops out when there are no rows - no heading, no
          empty line. Same rule AKL-780 used for an empty `models` list: render
          nothing rather than an empty label. The popover still shows its
          header, the reviewed chip and the actions, so it is never blank. */}
      {/* Legacy fallback: an API predating per-label dispositions. Every label
          fails closed out of `speciesRows`, so without this the popover would
          show no candidate list at all - the "Raw AI detection" list it used to
          show having been retired. Read-only by construction: with no
          disposition we cannot know which of these a project has excluded, and
          offering one would be the exact hazard AKL-777 closes.

          Reuses the existing `Raw AI detection` key rather than adding copy -
          it was retired from the disposition-aware path, not deleted, and it
          still describes precisely what this branch renders: raw labels. */}
      {legacyLabels.length > 0 && (
        <>
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-off-black-light/50 dark:text-primary-off-white/40">
            {t("Raw AI detection")}
          </div>
          <ul
            className="mb-2 flex flex-col gap-1"
            data-testid="popover-legacy-list"
          >
            {legacyLabels.map((label, i) => (
              <li
                key={`${label.raw_label}-${i}`}
                className="flex items-center justify-between text-xs text-off-black-light/80 dark:text-primary-off-white/80"
              >
                <span className="truncate">
                  {label.resolved_display_name || label.raw_label}
                </span>
                <span className="ml-2 whitespace-nowrap">
                  {pct(label.score)}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}

      {/* AKL-717: an unresolved box has no candidate rows — every label on it
          was excluded and is deliberately never shown — so without this line
          the popover would be a header and two buttons with nothing between. */}
      {unresolved && (
        <p
          className="mb-2 text-xs leading-snug text-off-black-light/70 dark:text-primary-off-white/60"
          data-testid="popover-unresolved-note"
        >
          {t("No species suggested for this detection.")}
        </p>
      )}

      {speciesRows.length > 0 && (
        <>
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-off-black-light/50 dark:text-primary-off-white/40">
            {t("Species proposed")}
          </div>
          <div
            className="mb-2 flex flex-col gap-1"
            role={canPick ? "radiogroup" : undefined}
            aria-label={
              canPick ? t("Choose a species for this detection") : undefined
            }
            data-testid="popover-species-list"
          >
            {speciesRows.map((row) => {
              const selectable = isSelectableCandidate(row);
              const breakdown = modelBreakdown(t, i18n.language, row.models);
              const body = (
                <>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">
                      {selectable ? row.displayName : inertLabel(t, row)}
                    </span>
                    {breakdown && (
                      <span className="block truncate text-[10px] text-off-black-light/60 dark:text-primary-off-white/50">
                        {breakdown}
                      </span>
                    )}
                  </span>
                  <span className="ml-2 shrink-0 whitespace-nowrap">
                    {pct(row.maxScore)}
                  </span>
                </>
              );
              // An inert row is plain text: no radio, no label element, nothing
              // focusable. Making it a disabled control would put it in the tab
              // order and announce a choice the reviewer cannot make.
              if (!selectable || !onSelectTaxon) {
                return (
                  <div
                    key={row.key}
                    className={`flex items-start justify-between text-xs ${
                      selectable
                        ? "text-off-black-light/80 dark:text-primary-off-white/80"
                        : "text-off-black-light/50 dark:text-primary-off-white/40"
                    }`}
                    data-testid="popover-species-row"
                    data-disposition={row.disposition}
                  >
                    {body}
                  </div>
                );
              }
              return (
                <label
                  key={row.key}
                  className="flex cursor-pointer items-start justify-between gap-2 text-xs text-off-black-light/80 dark:text-primary-off-white/80"
                  data-testid="popover-species-row"
                  data-disposition={row.disposition}
                >
                  <input
                    type="radio"
                    className={`${tailwindClassnames.checkBox} shrink-0`}
                    name={radioGroupName}
                    // Controlled: the surface owns the selection. The `name`
                    // above still makes this a real native group, so arrow-key
                    // navigation works.
                    checked={selectedTaxonId === row.taxonId}
                    onChange={() =>
                      onSelectTaxon(row.taxonId as number, row.displayName)
                    }
                    data-testid="popover-species-radio"
                  />
                  {body}
                </label>
              );
            })}
          </div>
        </>
      )}

      {prediction?.ai_model && (
        <div className="text-[11px] text-off-black-light/70 border-t border-black/10 pt-2 dark:text-primary-off-white/70 dark:border-white/10">
          {t("Model: {{name}}", { name: prediction.ai_model.name })}
          {prediction.ai_model.version && ` (${prediction.ai_model.version})`}
        </div>
      )}

      {hasActions && (
        <div className="mt-3 border-t border-black/10 pt-2 dark:border-white/10">
          {reviewed && (
            <p className="mb-2 text-[10px] text-off-black-light/60 dark:text-primary-off-white/50">
              {reviewed.rejected
                ? t(
                    "Acts on this box's rejection, not the raw AI detection above.",
                  )
                : t(
                    "Acts on the reviewed result above, not the raw AI detection.",
                  )}
            </p>
          )}
          <div className="flex gap-2">
            {/* AKL-717: nothing to accept on an unresolved box — no species was
                proposed — so Accept is withheld even if a handler slips through. */}
            {!isCommitted && !unresolved && onAccept && (
              <button
                type="button"
                className={`relative flex-1 rounded bg-green-600/90 px-2 py-1 text-xs font-semibold text-white hover:bg-green-600 focus-visible:ring-2 focus-visible:ring-green-400 focus:outline-none ${
                  pending === "accept" ? "" : "disabled:opacity-40"
                }`}
                onClick={onAccept}
                disabled={busy}
                data-testid="popover-accept"
              >
                {/* Hide (but keep) the label while spinning so the button width
                    is stable; white spinner reads on the green fill. */}
                <span
                  className={pending === "accept" ? "invisible" : undefined}
                >
                  {t("Accept")}
                </span>
                {pending === "accept" && (
                  <span className="absolute inset-0 flex items-center justify-center">
                    <BeatLoader
                      color="#FFFFFF"
                      loading={true}
                      size={6}
                      margin={2}
                    />
                  </span>
                )}
              </button>
            )}
            {!isCommitted && onReject && (
              <button
                type="button"
                className={`relative flex-1 rounded bg-red-600/90 px-2 py-1 text-xs font-semibold text-white hover:bg-red-600 focus-visible:ring-2 focus-visible:ring-red-400 focus:outline-none ${
                  pending === "reject" ? "" : "disabled:opacity-40"
                }`}
                onClick={onReject}
                disabled={busy}
                data-testid="popover-reject"
              >
                <span
                  className={pending === "reject" ? "invisible" : undefined}
                >
                  {t("Reject")}
                </span>
                {pending === "reject" && (
                  <span className="absolute inset-0 flex items-center justify-center">
                    <BeatLoader
                      color="#FFFFFF"
                      loading={true}
                      size={6}
                      margin={2}
                    />
                  </span>
                )}
              </button>
            )}
            {onEdit && (
              <button
                type="button"
                className="flex-1 rounded bg-black/10 px-2 py-1 text-xs font-semibold text-off-black-light hover:bg-black/20 focus-visible:ring-2 focus-visible:ring-sky-blue-light focus:outline-none disabled:opacity-40 dark:bg-white/10 dark:text-primary-off-white dark:hover:bg-white/20"
                onClick={onEdit}
                disabled={busy}
                // AKL-717: its own test id, so a test can never mistake
                // assigning a first species for editing an existing one.
                data-testid={
                  unresolved ? "popover-assign-species" : "popover-edit"
                }
              >
                {unresolved ? t("Assign species") : t("Edit")}
              </button>
            )}
            {isCommitted && onDelete && (
              <button
                type="button"
                className="flex-1 rounded bg-red-600/90 px-2 py-1 text-xs font-semibold text-white hover:bg-red-600 focus-visible:ring-2 focus-visible:ring-red-400 focus:outline-none disabled:opacity-40"
                onClick={onDelete}
                disabled={busy}
                data-testid="popover-delete"
              >
                {t("Delete")}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PredictionPopover;
